import { useState, useEffect, useRef } from "react";
import { PrayerTimes } from "../types/PrayerTypes";
import { ScreenData } from "../types/ScreenTypes";
import { getPrayerLength } from "../utils/prayerTimeUtils";

interface FlashSalahChangesState {
  /** Whether a prayer's time is currently flashing */
  isFlashing: boolean;
  /** Which prayer key is flashing (e.g. "JamDhuhr") */
  flashingPrayer: string | null;
}

const TARGET_PRAYERS = ["JamFajr", "JamDhuhr", "JamAsar", "JamEsha"] as const;

function parseTimeToDate(timeStr: string): Date | null {
  if (!timeStr || timeStr === "--:--") return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Hook that detects when a prayer has just ended and that prayer's jamaat time
 * is changing tomorrow. Returns which prayer should flash on the timetable.
 *
 * Same detection logic as useAfterPrayerSalahChanges but controlled by its own
 * settings (flashSalahChangesAfterPrayer, flashSalahChangesDuration).
 */
export function useFlashSalahChanges(
  prayerTimes: PrayerTimes | null,
  tomorrowPrayerTimes: PrayerTimes | null,
  screenData: ScreenData | null,
  enabled: boolean,
): FlashSalahChangesState {
  const [state, setState] = useState<FlashSalahChangesState>({
    isFlashing: false,
    flashingPrayer: null,
  });

  const shownForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !prayerTimes || !tomorrowPrayerTimes || !screenData) {
      if (state.isFlashing) {
        setState({ isFlashing: false, flashingPrayer: null });
      }
      return;
    }

    const duration = screenData.flashSalahChangesDuration ?? 30;
    const isFriday = new Date().getDay() === 5;

    const checkInterval = setInterval(() => {
      const now = new Date();

      for (const prayerKey of TARGET_PRAYERS) {
        if (prayerKey === "JamDhuhr" && isFriday) continue;

        const todayTime = prayerTimes[prayerKey as keyof PrayerTimes] as string;
        const tomorrowTime = tomorrowPrayerTimes[
          prayerKey as keyof PrayerTimes
        ] as string;

        if (
          !todayTime ||
          !tomorrowTime ||
          todayTime === "--:--" ||
          tomorrowTime === "--:--"
        )
          continue;
        if (todayTime === tomorrowTime) continue;

        const jamaatDate = parseTimeToDate(todayTime);
        if (!jamaatDate) continue;

        const prayerLengthSec = getPrayerLength(prayerKey, screenData);
        const windowStart = new Date(
          jamaatDate.getTime() + prayerLengthSec * 1000,
        );
        const windowEnd = new Date(windowStart.getTime() + duration * 1000);

        if (now >= windowStart && now <= windowEnd) {
          if (shownForRef.current !== prayerKey) {
            shownForRef.current = prayerKey;
          }
          setState({ isFlashing: true, flashingPrayer: prayerKey });
          return;
        }
      }

      // No prayer in flash window
      const inAnyWindow = TARGET_PRAYERS.some((key) => {
        if (key === "JamDhuhr" && isFriday) return false;
        const todayTime = prayerTimes[key as keyof PrayerTimes] as string;
        if (!todayTime || todayTime === "--:--") return false;
        const jamaatDate = parseTimeToDate(todayTime);
        if (!jamaatDate) return false;
        const prayerLengthSec = getPrayerLength(key, screenData);
        const windowEnd = new Date(
          jamaatDate.getTime() + (prayerLengthSec + duration) * 1000,
        );
        return now <= windowEnd && now >= jamaatDate;
      });

      if (!inAnyWindow) {
        shownForRef.current = null;
      }

      setState({ isFlashing: false, flashingPrayer: null });
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [enabled, prayerTimes, tomorrowPrayerTimes, screenData, state.isFlashing]);

  return state;
}
